import React, { useEffect, useRef, useState } from 'react';
import { Check, FileCheck2, Filter, Loader2, Search, X } from 'lucide-react';
import { TestamentTypeModel, TestamentTypeRepository } from '../../types';
import VirtualList from '../atoms/VirtualList';

interface TestamentRequirementSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (testamentTypes: TestamentTypeModel[]) => void;
  repository?: TestamentTypeRepository;
  excludedIds: string[];
}

const TestamentRequirementSelectionModal: React.FC<TestamentRequirementSelectionModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <TestamentRequirementSelectionModalInner {...props} />;
};

const TestamentRequirementSelectionModalInner: React.FC<TestamentRequirementSelectionModalProps> = ({
  onClose,
  onConfirm,
  repository,
  excludedIds
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [testamentTypes, setTestamentTypes] = useState<TestamentTypeModel[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const excludedIdSet = new Set(excludedIds);

  useEffect(() => {
    fetchTestamentTypes('');
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const fetchTestamentTypes = async (query: string) => {
    if (!repository) return;
    setLoading(true);
    try {
      const result = await repository.search(query);
      setTestamentTypes(result);
    } catch (err) {
      console.error('Failed to fetch testament types', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchTestamentTypes(value);
    }, 300);
  };

  const toggleSelection = (id: string) => {
    if (excludedIdSet.has(id)) return;

    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleConfirm = () => {
    onConfirm(testamentTypes.filter(type => selectedIds.has(type.testamentTypeId)));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[75vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Add Requirements</h2>
            <div className="text-xs text-slate-500 mt-1">{selectedIds.size} testament types selected</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100 flex gap-4 bg-white flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search testament types by name..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-slate-600 text-sm min-w-[100px] justify-center">
            {loading ? (
              <Loader2 size={16} className="animate-spin text-teal-600" />
            ) : (
              <>
                <Filter size={16} />
                <span className="font-medium">{testamentTypes.length} Found</span>
              </>
            )}
          </div>
        </div>

        <div className="bg-white border-b border-slate-200 shadow-sm z-10 flex-shrink-0">
          <div className="flex text-left text-sm">
            <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[60px] text-center">
              <div className="w-4 h-4 mx-auto border-2 border-slate-300 rounded"></div>
            </div>
            <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider flex-1">Testament Type</div>
            <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[25%] hidden md:block">Category</div>
            <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[25%]">Properties</div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-50 relative">
          {testamentTypes.length === 0 && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
              <Search size={32} className="mx-auto mb-2 opacity-20" />
              No testament types found matching "{searchTerm}"
            </div>
          )}
          <VirtualList<TestamentTypeModel>
            items={testamentTypes}
            height="100%"
            itemHeight={72}
            renderItem={(testamentType) => {
              const id = testamentType.testamentTypeId;
              const isSelected = selectedIds.has(id);
              const isAlreadyAdded = excludedIdSet.has(id);

              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={isAlreadyAdded ? -1 : 0}
                  onClick={() => toggleSelection(id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelection(id); } }}
                  className={`
                    flex items-center text-sm border-b border-slate-100 h-[72px] transition-colors
                    ${isAlreadyAdded ? 'bg-slate-100 cursor-not-allowed opacity-60' : 'cursor-pointer'}
                    ${isSelected ? 'bg-teal-50 hover:bg-teal-100' : 'bg-white hover:bg-slate-50'}
                  `}
                >
                  <div className="px-4 w-[60px] text-center flex-shrink-0">
                    <div className={`
                      w-4 h-4 mx-auto rounded border flex items-center justify-center transition-all
                      ${isSelected ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300 bg-white'}
                    `}>
                      {isSelected && <Check size={10} strokeWidth={4} />}
                    </div>
                  </div>
                  <div className="px-4 flex-1 min-w-0">
                    <div className={`font-medium truncate ${isSelected ? 'text-teal-900' : 'text-slate-900'}`}>
                      {testamentType.name}
                    </div>
                    <div className="flex gap-2 text-[10px] mt-1">
                      <span className="font-mono text-slate-400">{id}</span>
                      {isAlreadyAdded && <span className="bg-slate-200 px-1 rounded text-slate-500 border border-slate-300">Already added</span>}
                      {testamentType.isDeprecated && <span className="bg-amber-50 text-amber-700 px-1 rounded border border-amber-200">Deprecated</span>}
                    </div>
                  </div>
                  <div className="px-4 w-[25%] hidden md:block text-slate-500 truncate">
                    {testamentType.category || <span className="text-slate-300 italic">Uncategorized</span>}
                  </div>
                  <div className="px-4 w-[25%] flex flex-wrap gap-1.5 text-[10px] text-slate-500">
                    {testamentType.requiresDocument && (
                      <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100 flex items-center gap-1">
                        <FileCheck2 size={9} /> Document
                      </span>
                    )}
                    {testamentType.isRenewable && (
                      <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-100">Renewable</span>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-white flex justify-between items-center flex-shrink-0">
          <div className="text-sm text-slate-500">
            {selectedIds.size > 0 && (
              <span className="font-medium text-slate-900">{selectedIds.size} requirements selected</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 font-medium text-sm hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
              className="bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-400 text-white px-6 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm shadow-teal-600/20"
            >
              <Check size={18} /> Add Requirements
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestamentRequirementSelectionModal;
